import React, { useEffect, useRef, useState, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Share, ArrowLeft, Briefcase, GraduationCap, Award, Heart, Sparkles, Globe, Ban, RotateCcw, SquareUserRound, FileText, Code, CheckCircle } from 'lucide-react';
import { toast, Toaster } from './Toast';
import DeveloperProfile from './DeveloperProfile';
import { SoftwareTalent } from '../types';
import { DomainType, saveTalent, unsaveTalent, discardTalent, restoreTalent } from '../services/api';
import { cn } from '../lib/utils';
import { useTheme } from '../context/ThemeContext';
import { isAcademicDomain } from '../config/appConfig';

const SECTION_NAV_ITEMS: { id: string; label: string; icon: React.ElementType }[] = [
  { id: 'info', label: 'Info', icon: SquareUserRound },
  { id: 'highlights', label: 'Highlights', icon: Sparkles },
  { id: 'publications', label: 'Publications', icon: FileText },
  { id: 'vetting', label: 'Vetting', icon: Award },
  { id: 'experience', label: 'Experience', icon: Briefcase },
  { id: 'education', label: 'Education', icon: GraduationCap },
  { id: 'certifications', label: 'Certifications', icon: CheckCircle },
  { id: 'projects', label: 'Projects', icon: Code },
  { id: 'languages', label: 'Languages', icon: Globe },
  { id: 'hobbies', label: 'Hobbies', icon: Heart },
];

const STEM_SECTION_NAV_ITEMS: { id: string; label: string; icon: React.ElementType }[] = [
  { id: 'info', label: 'Info', icon: SquareUserRound },
  { id: 'highlights', label: 'Highlights', icon: Sparkles },
  { id: 'vetting', label: 'Vetting', icon: Award },
  { id: 'education', label: 'Education', icon: GraduationCap },
  { id: 'experience', label: 'Experience', icon: Briefcase },
  { id: 'publications', label: 'Publications', icon: FileText },
  { id: 'certifications', label: 'Certifications', icon: CheckCircle },
  { id: 'projects', label: 'Projects', icon: Code },
  { id: 'languages', label: 'Languages', icon: Globe },
  { id: 'hobbies', label: 'Hobbies', icon: Heart },
];

interface QuickViewModalProps {
  /** Currently displayed talent */
  talent: SoftwareTalent;
  domain: DomainType;
  /** List of all loaded talent for prev/next (filtered by domain on action tabs) */
  allTalent: SoftwareTalent[];
  /** Close modal and go back to list */
  onClose: () => void;
  /** Navigate to full profile page (/candidate/:id) - optional, View full profile button removed */
  onViewFullProfile?: () => void;
  /** Navigate to previous/next talent */
  onNavigateTalent: (talent: SoftwareTalent) => void;
  /** Whether there are more results to load */
  hasMore?: boolean;
  /** Load more results (for Next at end of list) */
  onLoadMore?: () => Promise<void>;
  loadingMore?: boolean;
  /** Save/unsave/discard/restore handlers */
  onView?: (talentId: number, domain?: DomainType) => void;
  onSave?: (talentId: number, domain?: DomainType) => void;
  onUnsave?: (talentId: number, domain?: DomainType) => void;
  onDiscard?: (talentId: number, domain?: DomainType) => void;
  onRestore?: (talentId: number, domain?: DomainType) => void;
  isSaved?: boolean;
  isDiscarded?: boolean;
  discardedIds?: Set<number>;
  activeTab?: 'search' | 'saved' | 'viewed' | 'discarded';
  readOnly?: boolean;
  /** Admin flows: skip tid so cross-tenant profiles resolve via /developers/:id. */
  bypassTenant?: boolean;
}

const QuickViewModal: React.FC<QuickViewModalProps> = ({
  talent,
  domain,
  allTalent,
  onClose,
  onNavigateTalent,
  hasMore = false,
  onLoadMore,
  loadingMore = false,
  onView,
  onSave,
  onUnsave,
  onDiscard,
  onRestore,
  isSaved = false,
  isDiscarded = false,
  discardedIds = new Set(),
  activeTab = 'search',
  readOnly = false,
  bypassTenant = false,
}) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const sectionNavItems = isAcademicDomain(domain) ? STEM_SECTION_NAV_ITEMS : SECTION_NAV_ITEMS;

  const currentIndex = allTalent.findIndex((t) => t.id === talent.id);
  const prevTalent = currentIndex > 0 ? allTalent[currentIndex - 1] : null;
  const nextTalentInList = currentIndex >= 0 && currentIndex < allTalent.length - 1 ? allTalent[currentIndex + 1] : null;
  const atEndOfLoadedList = nextTalentInList === null && hasMore;
  const hasNext = nextTalentInList !== null || atEndOfLoadedList;
  const hasPrevious = prevTalent !== null;

  const prevTalentLengthRef = useRef(allTalent.length);
  const [pendingNext, setPendingNext] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [activeSection, setActiveSection] = useState('info');
  const [availableSections, setAvailableSections] = useState<string[]>(['info']);
  const availableSectionsRef = useRef<string[]>(['info']);
  const [sectionLabelOverrides, setSectionLabelOverrides] = useState<Record<string, string>>({});
  const [isAnimatingIn, setIsAnimatingIn] = useState(false);
  const [isDismissed] = useState(false);

  // Trigger fade-in animation on mount
  useEffect(() => {
    const timer = setTimeout(() => setIsAnimatingIn(true), 10);
    return () => clearTimeout(timer);
  }, []);

  const scrollToSection = useCallback((sectionId: string) => {
    const el = scrollContainerRef.current?.querySelector(`[data-section-id="${sectionId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(sectionId);
    }
  }, []);

  // Update active section on scroll (only consider available sections)
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const handleScroll = () => {
      const top = container.scrollTop + 80;
      let current = availableSections[0] || 'info';
      for (const id of availableSections) {
        const el = container.querySelector(`[data-section-id="${id}"]`);
        if (el) {
          const rect = el.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          const elTop = rect.top - containerRect.top + container.scrollTop;
          if (elTop <= top) current = id;
        }
      }
      setActiveSection(current);
    };
    container.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => container.removeEventListener('scroll', handleScroll);
  }, [talent.id, availableSections]);

  // Auto-navigate to next talent when new talent is loaded after clicking Next at end
  useEffect(() => {
    if (pendingNext && allTalent.length > prevTalentLengthRef.current) {
      const next = allTalent[currentIndex + 1];
      if (next) {
        onNavigateTalent(next);
        setPendingNext(false);
      }
    }
    prevTalentLengthRef.current = allTalent.length;
  }, [allTalent, pendingNext, currentIndex, onNavigateTalent]);

  const handlePrev = useCallback(() => {
    if (prevTalent) onNavigateTalent(prevTalent);
  }, [prevTalent, onNavigateTalent]);

  const handleNext = useCallback(async () => {
    if (nextTalentInList) {
      onNavigateTalent(nextTalentInList);
    } else if (atEndOfLoadedList && onLoadMore) {
      setPendingNext(true);
      await onLoadMore();
    }
  }, [nextTalentInList, atEndOfLoadedList, onLoadMore, onNavigateTalent]);

  // Keyboard: Escape to close, Arrow keys for prev/next
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft' && hasPrevious && prevTalent) {
        e.preventDefault();
        handlePrev();
      } else if (e.key === 'ArrowRight' && hasNext && !loadingMore) {
        e.preventDefault();
        handleNext();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [hasPrevious, hasNext, prevTalent, loadingMore, onClose, handlePrev, handleNext]);

  // Prevent body scroll when modal is open.
  // Use overflow:hidden only (not position:fixed) so that touch scrolling works inside the modal.
  // position:fixed on body blocks touch scroll in iOS Safari and other mobile browsers.
  // On close, restore to current scroll position (which may have been updated by background
  // scroll-into-view when navigating between talents).
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  // Forward wheel/touch scroll from backdrop & header to the profile scroll container.
  // This ensures scrolling anywhere on the overlay scrolls the profile, not the listings underneath.
  useEffect(() => {
    const overlay = overlayRef.current;
    if (!overlay) return;

    const handleWheel = (e: WheelEvent) => {
      const container = scrollContainerRef.current;
      if (!container) return;
      if (container.contains(e.target as Node)) return;
      e.preventDefault();
      container.scrollTop += e.deltaY;
    };

    let touchStartY = 0;
    const handleTouchStart = (e: TouchEvent) => {
      const container = scrollContainerRef.current;
      if (container?.contains(e.target as Node)) return;
      touchStartY = e.touches[0].clientY;
    };
    const handleTouchMove = (e: TouchEvent) => {
      const container = scrollContainerRef.current;
      if (!container || container.contains(e.target as Node)) return;
      const deltaY = touchStartY - e.touches[0].clientY;
      touchStartY = e.touches[0].clientY;
      container.scrollTop += deltaY;
      e.preventDefault();
    };

    overlay.addEventListener('wheel', handleWheel, { passive: false });
    overlay.addEventListener('touchstart', handleTouchStart, { passive: true });
    overlay.addEventListener('touchmove', handleTouchMove, { passive: false });
    return () => {
      overlay.removeEventListener('wheel', handleWheel);
      overlay.removeEventListener('touchstart', handleTouchStart);
      overlay.removeEventListener('touchmove', handleTouchMove);
    };
  }, []);

  const handleCopyLink = useCallback(() => {
    const tid = localStorage.getItem('tid');
    const tidParam = tid ? `&tid=${tid}` : '';
    const url = `${window.location.origin}/candidate/${talent.id}?domain=${encodeURIComponent(domain)}${tidParam}`;
    const doCopy = () => {
      try {
        if (navigator.clipboard?.writeText) {
          navigator.clipboard.writeText(url).then(() => {
            toast.success('Link copied to clipboard');
          }).catch(doCopyFallback);
        } else {
          doCopyFallback();
        }
      } catch {
        doCopyFallback();
      }
    };
    const doCopyFallback = () => {
      const textarea = document.createElement('textarea');
      textarea.value = url;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      toast.success('Link copied to clipboard');
    };
    doCopy();
  }, [talent.id, domain]);

  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const handleSave = useCallback(async () => {
    if (actionLoading) return;
    setActionLoading('save');
    try {
      if (isSaved) {
        await unsaveTalent(talent.id, domain);
        onUnsave?.(talent.id, domain);
      } else {
        await saveTalent(talent.id, domain);
        onSave?.(talent.id, domain);
      }
    } catch (err) {
      console.error('Failed to save/unsave talent:', err);
    } finally {
      setActionLoading(null);
    }
  }, [actionLoading, isSaved, onSave, onUnsave, talent.id, domain]);

  const handleDiscard = useCallback(async () => {
    if (actionLoading) return;
    setActionLoading('discard');
    try {
      if (isDiscarded) {
        await restoreTalent(talent.id, domain);
        onRestore?.(talent.id, domain);
      } else {
        await discardTalent(talent.id, domain);
        onDiscard?.(talent.id, domain);
      }
    } catch (err) {
      console.error('Failed to discard/restore talent:', err);
    } finally {
      setActionLoading(null);
    }
  }, [actionLoading, isDiscarded, onDiscard, onRestore, talent.id, domain]);

  // Detect which sections exist in DOM (only show those in nav)
  useEffect(() => {
    const checkSections = () => {
      const container = scrollContainerRef.current;
      if (!container) return;
      const sectionEls = container.querySelectorAll('[data-section-id]');
      const idsSet = new Set<string>();
      const labelMap: Record<string, string> = {};
      sectionEls.forEach((el) => {
        const id = el.getAttribute('data-section-id');
        if (id) {
          idsSet.add(id);
          const customLabel = el.getAttribute('data-section-label');
          if (customLabel) labelMap[id] = customLabel;
        }
      });
      if (idsSet.size > 0) {
        const ordered = sectionNavItems.filter((item) => idsSet.has(item.id)).map((item) => item.id);
        const same = ordered.length === availableSectionsRef.current.length && ordered.every((id, i) => id === availableSectionsRef.current[i]);
        if (!same) {
          availableSectionsRef.current = ordered;
          setAvailableSections(ordered);
        }
        setSectionLabelOverrides(labelMap);
      }
    };
    const timer = setTimeout(checkSections, 500);
    const observer = new MutationObserver(checkSections);
    const container = scrollContainerRef.current;
    if (container) {
      observer.observe(container, { childList: true, subtree: true });
    }
    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [talent.id, sectionNavItems]);

  return (
    <div ref={overlayRef} className="fixed inset-0 z-50 flex items-stretch justify-center p-0 md:p-6 md:items-center">
      <Toaster />
      {/* Backdrop - split for prev/next button zones (clicking sides doesn't close) */}
      <div className={`absolute left-0 top-0 bottom-0 w-16 md:w-20 bg-black/50 transition-opacity duration-300 ${isDismissed ? 'opacity-0' : 'opacity-100'}`} aria-hidden />
      <div className={`absolute right-0 top-0 bottom-0 w-16 md:w-20 bg-black/50 transition-opacity duration-300 ${isDismissed ? 'opacity-0' : 'opacity-100'}`} aria-hidden />
      <div
        className={`absolute left-16 md:left-20 right-16 md:right-20 top-0 bottom-0 bg-black/50 transition-opacity duration-300 ${isDismissed ? 'opacity-0' : 'opacity-100'}`}
        onClick={onClose}
        aria-hidden
      />

      {/* Prev button - left side */}
      {hasPrevious && prevTalent && (
        <button
          onClick={handlePrev}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-50 w-12 h-12 flex items-center justify-center rounded-full bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 shadow-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
          aria-label="Previous candidate"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
      )}

      {/* Next button - right side */}
      {hasNext && (
        <button
          onClick={handleNext}
          disabled={loadingMore}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-50 w-12 h-12 flex items-center justify-center rounded-full bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 shadow-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          aria-label="Next candidate"
        >
          {loadingMore ? (
            <span className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
          ) : (
            <ChevronRight className="w-6 h-6" />
          )}
        </button>
      )}

      {/* Modal content - h-full/md:h-[90vh] gives definite height for scroll.
          min-h-0 allows flex child to shrink. */}
      <div
        className={`relative w-full h-full md:h-auto md:max-h-[90vh] md:max-w-5xl min-h-0 bg-slate-50 dark:bg-slate-950 md:rounded-xl shadow-2xl overflow-hidden flex flex-col transition-all duration-300 ease-out ${isDismissed ? 'opacity-0' : isAnimatingIn ? 'opacity-100' : 'opacity-0'}`}
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header - Back, Share, Save, Discard (stays at top, not in scroll area) */}
        <div className="flex-shrink-0 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 z-10 shadow-sm">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <button
                onClick={onClose}
                className="flex items-center gap-2 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="font-medium">Back</span>
              </button>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleCopyLink}
                  className="flex items-center justify-center w-10 h-10 rounded-lg text-slate-700 dark:text-slate-300 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                  title="Copy share link"
                >
                  <Share className="w-4 h-4" />
                </button>
                {!readOnly && (
                  <>
                    <button
                      onClick={handleSave}
                      disabled={actionLoading !== null}
                      className={cn(
                        'flex items-center gap-2 px-4 py-2 rounded-lg transition-colors',
                        isSaved ? 'font-semibold' : 'font-medium'
                      )}
                      style={{
                        backgroundColor: isDark
                          ? (isSaved ? 'rgba(127, 29, 29, 0.3)' : 'rgba(127, 29, 29, 0.15)')
                          : (isSaved ? '#fef2f2' : '#fff5f5'),
                        color: isDark ? '#f87171' : '#ef4444',
                        border: `1px solid ${isDark ? '#f87171' : '#ef4444'}`,
                      }}
                    >
                      {actionLoading === 'save' ? (
                        <span className="inline-block w-4 h-4 border-[2.5px] border-current border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Heart className={cn('w-4 h-4', isSaved && 'fill-current')} />
                      )}
                      {isSaved ? 'Saved' : 'Save'}
                    </button>
                    <button
                      onClick={handleDiscard}
                      disabled={actionLoading !== null}
                      className={cn(
                        'flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors',
                        isDiscarded
                          ? 'bg-amber-50 dark:bg-amber-950/30 text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-900/50 hover:bg-amber-100 dark:hover:bg-amber-950/50'
                          : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-300 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
                      )}
                    >
                      {actionLoading === 'discard' ? (
                        <span className="inline-block w-4 h-4 border-[2.5px] border-current border-t-transparent rounded-full animate-spin" />
                      ) : isDiscarded ? (
                        <RotateCcw className="w-4 h-4" />
                      ) : (
                        <Ban className="w-4 h-4" />
                      )}
                      {isDiscarded ? 'Restore' : 'Discard'}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Section navigation - stays at top, only show sections that have data */}
        <div className="flex-shrink-0 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 z-10 px-4 md:px-6">
          <div className="overflow-x-auto scrollbar-hide">
            <div className="flex items-center gap-0 min-w-max" role="tablist">
              {sectionNavItems.filter((item) => item.id !== 'highlights' && availableSections.includes(item.id)).map((item) => {
                const Icon = item.icon;
                const isActive = activeSection === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    role="tab"
                    aria-selected={isActive}
                    className={cn(
                      'relative flex items-center gap-1.5 px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors',
                      isActive ? 'text-slate-900 dark:text-slate-100' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
                    )}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{sectionLabelOverrides[item.id] || item.label}</span>
                    {isActive && (
                      <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-slate-900 dark:bg-slate-100 rounded-full" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Scrollable content - flex-1 min-h-0 overflow-y-auto.
            min-h-0 is required for flex child to shrink and allow overflow scroll. */}
        <div
          ref={scrollContainerRef}
          className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain"
          style={{
            touchAction: 'pan-y',
            WebkitOverflowScrolling: 'touch',
          } as React.CSSProperties}
        >
          <div className="px-4 py-4 md:px-6 md:py-6 min-h-full">
            <DeveloperProfile
              talentId={talent.id}
              domain={domain}
              onBack={onClose}
              searchTalent={talent}
              allTalent={allTalent}
              onNavigateTalent={onNavigateTalent}
              onView={readOnly ? undefined : onView}
              onSave={readOnly ? undefined : onSave}
              onUnsave={readOnly ? undefined : onUnsave}
              onDiscard={readOnly ? undefined : onDiscard}
              onRestore={readOnly ? undefined : onRestore}
              isSaved={readOnly ? false : isSaved}
              isDiscarded={readOnly ? false : isDiscarded}
              discardedIds={readOnly ? new Set() : discardedIds}
              skipViewTracking={readOnly}
              hasMore={hasMore}
              onLoadMore={onLoadMore}
              loadingMore={loadingMore}
              activeTab={activeTab}
              hideHeader={true}
              collapsibleSections={true}
              bypassTenant={bypassTenant}
            />
          </div>
        </div>
      </div>
    </div>
  );
};


export default QuickViewModal;
